import { EmptyState } from "@salesapp/ui";
import { useLocale } from "../../shell/LocaleProvider";

export interface RecordChange {
  id: string;
  action: string;
  field_key: string;
  field_label?: string | null;
  old_value?: string | null;
  new_value?: string | null;
  actor_name?: string | null;
  actor_user_id?: string | null;
  created_at?: string | null;
  record_code?: string | null;
}

const ACTION_LABELS: Record<string, string> = {
  create: "Created",
  update: "Updated",
  revise: "Revised",
  status_change: "Status change",
  stage_change: "Stage change",
  convert: "Converted",
  revert_to_lead: "Moved back to lead",
  disqualify: "Disqualified",
};

const VALUE_LABELS: Record<string, Record<string, string>> = {
  stage: {
    lead_in: "Lead In",
    qualifying: "Qualifying",
    proposal: "Proposal",
    negotiation: "Negotiation",
    closed_won: "Closed Won",
    closed_lost: "Closed Lost",
  },
  lead_status: {
    new: "New",
    working: "Working",
    qualified: "Qualified",
    converted: "Converted",
    disqualified: "Disqualified",
  },
};

function displayValue(change: RecordChange, which: "old" | "new"): string {
  const raw = which === "old" ? change.old_value : change.new_value;
  if (!raw) return "—";
  return VALUE_LABELS[change.field_key]?.[raw] ?? raw;
}

export function ChangeHistoryPanel({
  changes,
  title = "Change history",
  emptyTitle = "No changes yet",
  emptyDescription = "Edits, stage moves, and conversions will appear here with the old and new values.",
}: {
  changes: RecordChange[];
  title?: string;
  emptyTitle?: string;
  emptyDescription?: string;
}) {
  const { formatDateTime } = useLocale();
  if (changes.length === 0) {
    return <EmptyState title={emptyTitle} description={emptyDescription} />;
  }
  return (
    <div className="crm-table-card">
      <h2 className="crm-section-title">{title}</h2>
      <p className="crm-muted">
        {changes.length} {changes.length === 1 ? "item" : "items"} · Sorted by last modified
      </p>
      <table className="crm-table" aria-label={title}>
        <thead>
          <tr>
            <th>#</th>
            <th>When</th>
            <th>Change</th>
            <th>Field</th>
            <th>Old value</th>
            <th>New value</th>
            <th>Changed by</th>
          </tr>
        </thead>
        <tbody>
          {changes.map((change, index) => (
            <tr key={change.id}>
              <td>{index + 1}</td>
              <td>{formatDateTime(change.created_at)}</td>
              <td>{ACTION_LABELS[change.action] ?? change.action}</td>
              <td>{change.field_label || change.field_key}</td>
              <td>{displayValue(change, "old")}</td>
              <td>{displayValue(change, "new")}</td>
              <td>{change.actor_name || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
